/**
 * useReportFilters.js
 * Manages report run state: filters, cascading sale filter data,
 * sales/accounts lists, runReport, canRunReport.
 * Extracted from ReportsPage.js — Stage 2 logic refactor.
 */
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';

export function useReportFilters(activeReport) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sales, setSales] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [filters, setFilters] = useState({
    sale_id: '', crm_entity_id: '', opportunity_id: '', session_id: '',
    date: new Date().toLocaleDateString('sv'), // default to today in local tz
    group_by: 'sire', start_date: '', end_date: '',
  });
  const [saleFilterData, setSaleFilterData] = useState({ accounts: [], opportunities: [], sessions: [] });
  const [loadingFilterData, setLoadingFilterData] = useState(false);

  // Load sales and accounts once on mount
  useEffect(() => {
    api.get('/sales').then(r => setSales(r.data || [])).catch(() => {});
    // Fetch CRM accounts + tenant (Consignor) for the account filter
    const userStr = localStorage.getItem('user');
    const tenantId = userStr ? JSON.parse(userStr).tenant_id : null;
    Promise.all([
      api.get('/crm-entities?crm_class=account'),
      tenantId ? api.get(`/tenants/${tenantId}`).catch(() => null) : Promise.resolve(null),
    ]).then(([crmRes, tenantRes]) => {
      const crmAccounts = crmRes.data || [];
      if (tenantRes?.data?.id) {
        crmAccounts.push({ id: tenantRes.data.id, system_name: tenantRes.data.name + ' (Consignor)' });
      }
      setAccounts(crmAccounts);
    }).catch(() => {});
  }, []);

  // Fetch cascading filter data when sale or report date changes on daily-sale-updates
  useEffect(() => {
    if (activeReport !== 'daily-sale-updates' || !filters.sale_id) {
      setSaleFilterData({ accounts: [], opportunities: [], sessions: [] });
      return;
    }
    // Clear stale horse and session selections when sale or date changes
    setFilters(f => ({ ...f, opportunity_id: '', session_id: '' }));
    setLoadingFilterData(true);
    const localToday = new Date().toLocaleDateString('sv');
    const dateParam = filters.date || localToday;
    api.get(`/reports/sale-filter-data?sale_id=${filters.sale_id}&date=${dateParam}`)
      .then(r => setSaleFilterData(r.data || { accounts: [], opportunities: [] }))
      .catch(() => setSaleFilterData({ accounts: [], opportunities: [], sessions: [] }))
      .finally(() => setLoadingFilterData(false));
  }, [filters.sale_id, filters.date, activeReport]);

  const runReport = async (reportId, selectedFields) => {
    setLoading(true);
    setData(null);
    try {
      const params = new URLSearchParams();
      if (filters.sale_id)        params.append('sale_id', filters.sale_id);
      if (filters.crm_entity_id)  params.append('crm_entity_id', filters.crm_entity_id);
      // "__all__" means all horses for the selected owner — don't pass opportunity_id
      if (filters.opportunity_id && filters.opportunity_id !== '__all__') params.append('opportunity_id', filters.opportunity_id);
      // Session filter for DSU All-Accounts mode
      if (reportId === 'daily-sale-updates' && filters.session_id) params.append('session_id', filters.session_id);
      // For DSU: use selected date or today in LOCAL time (not UTC) so overnight UTC offset doesn't shift the date
      if (reportId === 'daily-sale-updates') {
        const localToday = new Date().toLocaleDateString('sv'); // 'sv' locale → YYYY-MM-DD in local tz
        params.append('date', filters.date || localToday);
      } else {
        if (filters.date) params.append('date', filters.date);
      }
      if (filters.start_date)     params.append('start_date', filters.start_date);
      if (filters.end_date)       params.append('end_date', filters.end_date);
      if (reportId === 'sales-performance') params.append('group_by', filters.group_by || 'sire');
      params.append('fields', selectedFields.join(','));
      const res = await api.get(`/reports/${reportId}?${params}`);
      setData(res.data);
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
      const status = error?.response?.status;
      console.error('[Reports] runReport failed:', status, detail, error);
      toast.error(`Failed to run report${status ? ` (${status})` : ''}: ${detail}`);
    }
    setLoading(false);
  };

  const canRunReport = (reportId) => {
    if (reportId === 'daily-sale-updates') {
      if (filters.crm_entity_id === '__all_accounts__') {
        return !!filters.sale_id;  // All Accounts: only sale required
      }
      return !!(filters.sale_id && filters.crm_entity_id && filters.opportunity_id);
    }
    return true;
  };

  return {
    data, setData,
    loading,
    filters, setFilters,
    sales, accounts,
    saleFilterData, loadingFilterData,
    runReport, canRunReport,
  };
}
